import React, { useEffect, useRef, useState } from 'react';
import * as THREE from 'three';
import { STLLoader } from 'three/examples/jsm/loaders/STLLoader';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import { TransformControls } from 'three/examples/jsm/controls/TransformControls';
import { STLExporter } from 'three/examples/jsm/exporters/STLExporter';
import TWEEN from '@tweenjs/tween.js';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'font-awesome/css/font-awesome.min.css';
import Swal from 'sweetalert2';
import './App.css';
/*
import { SimplifyModifier } from 'three/examples/jsm/modifiers/SimplifyModifier';
import { CSG } from 'three-csg-ts';  // Import CSG from the library
import { Tween, Easing, Group } from '@tweenjs/tween.js';  // Import TWEEN.js
import { mergeBufferGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { Plane } from 'three';
import { MeshBVH, acceleratedRaycast, computeBoundsTree, disposeBoundsTree } from 'three-mesh-bvh';
import { SUBTRACTION, Brush, Evaluator } from 'three-bvh-csg';
*/

const Loader = () => {
  return (
    <div style={styles2.loaderContainer}>
      <i className="fa fa-spinner fa-spin" style={styles2.spinner}></i>
    </div>
  );
};

// CSS styles as an object for the component
const styles2 = {
  loaderContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%', // Adjust height as needed
  },
  spinner: {
    fontSize: '30px', // Adjust size as needed
    color: '#ffffff', // Change color as needed
  },
};

const ModelSelector = ({ scene, onModelSelected }) => {
  const [models, setModels] = useState([]);
  const [loadingStates, setLoadingStates] = useState([]);
  const [selectedModelIndex, setSelectedModelIndex] = useState(null); // Track selected model
  const modelLoader = useRef(new STLLoader());

  // Models to load
  const modelPaths2 = [
    '/models/Stock Design Files/Upper/1U/1UG.stl',
    '/models/Stock Design Files/Upper/2U/2UG.stl',
    '/models/Stock Design Files/Upper/3U/3UG.stl',
    '/models/Stock Design Files/Lower/1L/1LG.stl',
    '/models/Stock Design Files/Lower/2L/2LG.stl',
    '/models/Stock Design Files/Lower/3L/3LG.stl',
  ];



  // Models to load (prepend the base URL)
  const modelPaths = [
    `/models/Stock Design Files/Upper/1U/1UG.stl`,
    `/models/Stock Design Files/Upper/2U/2UG.stl`,
  ];



  useEffect(() => {
    loadModelsSequentially();
  }, []);

  // Load models one by one
  const loadModelsSequentially = async () => {
    const loadedModels = [];
    const loadingStates = Array(modelPaths.length).fill(true);

    for (let i = 0; i < modelPaths.length; i++) {
      const model = await loadModel(modelPaths[i]);
      model.layerType = 'LayerB'; // Tag each model as Layer B
      loadedModels.push(model);
      loadingStates[i] = false; // Mark model as loaded
      setModels([...loadedModels]);
      setLoadingStates([...loadingStates]);
    }
  };

  // Load a specific STL model with optimized settings
  const loadModel = (path) => {
    return new Promise((resolve, reject) => {
      fetch(path)
        .then((response) => {
          if (!response.ok) throw new Error(`Failed to load model: ${response.statusText}`);
          return response.arrayBuffer();
        })
        .then((data) => {
          const geometry = modelLoader.current.parse(data);
          const material = new THREE.MeshStandardMaterial({
            color: 0xffff00, // Set color for Layer B
            metalness: 0.5,
            roughness: 0.5,
            side: THREE.DoubleSide
          });
          const mesh = new THREE.Mesh(geometry, material);
          mesh.name = path.split('/').pop(); // Set model name based on filename

          // Center the model
          const box = new THREE.Box3().setFromObject(mesh);
          const center = box.getCenter(new THREE.Vector3());
          mesh.position.sub(center); // Center the model

          // Scale the model to fit nicely within the grid cell
          const size = box.getSize(new THREE.Vector3()).length();
          const scaleFactor = 1.5 / size; // Adjust this factor based on your grid cell size
          mesh.scale.set(scaleFactor, scaleFactor, scaleFactor);

          mesh.position.set(0, 0, 0); // Center model

          resolve(mesh);
        })
        .catch(reject);
    });
  };

  // Render a 3D model into the canvas
  const renderModel = (model, canvas) => {
    if (!canvas) return;

    const renderer = new THREE.WebGLRenderer({ canvas, alpha: true });
    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(25, 1, 0.1, 1000);

    // Add lighting to the scene
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.5); // Soft white light
    const directionalLight = new THREE.DirectionalLight(0xffffff, 1); // Hard white light
    directionalLight.position.set(5, 5, 5).normalize(); // Position the light
    scene.add(ambientLight);
    scene.add(directionalLight);

    // Set camera position based on model size
    const box = new THREE.Box3().setFromObject(model);
    const size = box.getSize(new THREE.Vector3()).length();
    const cameraDistance = size * 2; // Set the camera distance based on model size
    camera.position.z = cameraDistance;

    // Add model to the scene
    scene.add(model);

    // Add OrbitControls for rotating the model
    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true; // Smooth rotation
    controls.dampingFactor = 0.1;
    controls.enableZoom = false; // Disable zoom for grid previews
    controls.update();

    // Render loop
    const animate = () => {
      requestAnimationFrame(animate);
      controls.update(); // Update controls for smooth rotation
      renderer.setSize(canvas.clientWidth, canvas.clientHeight); // Set renderer size based on canvas
      renderer.render(scene, camera);
    };
    animate();
  };

  // Handle model selection/unselection and visual feedback
  const handleModelClick = (index) => {

    if (selectedModelIndex === index) {
      // If clicked again, unselect the model
      setSelectedModelIndex(null);
      onModelSelected(null, false);  // Pass model to be removed from the scene
    } else {
      setSelectedModelIndex(index); // Track which model is selected
      models[index].layerType = 'LayerB'; // Explicitly mark as Layer B
      onModelSelected(models[index], true); // Pass the selected model to the main scene handler
    }

  };


  return (
    <div className="model-selector" style={styles.container}>
      {/* Header Title with an Icon */}
      <div className="d-flex align-items-center mb-3">
        <h4 className="me-2">
          <i className="fa fa-tooth text-warning"></i>
          Available Denture Models
        </h4>
      </div>

      {/* Description */}
      <p className="text-light">
        <i className="fa fa-info-circle me-2"></i>
        Select a model from the list below to view, modify, or adapt it according to your specifications.
      </p>

      <div style={styles.grid}>
        {modelPaths.map((_, index) => (
          <div
            key={index}
            className={`model-thumbnail btn ${selectedModelIndex === index ? 'btn-danger' : 'btn-secondary'}`}
            style={styles.thumbnail}
            onClick={() => handleModelClick(index)}
          >
            {loadingStates[index] ? (
              <div style={styles.loaderContainer}>
                <Loader /> {/* Display loader while loading */}
              </div>
            ) : (
              <canvas
                id={`modelCanvas-${index}`}
                style={styles.modelCanvas}
                ref={(canvas) => {
                  if (canvas && models[index]) {
                    renderModel(models[index], canvas); // Render the model into the canvas
                  }
                }}
              />
            )}

          </div>
        ))}
      </div>
    </div>
  );
};

// CSS styles as an object for the component
const styles = {
  container: {
    position: 'absolute',
    top: '20px',
    right: '20px',
    background: 'rgb(68, 68, 68)',
    padding: '10px',
    borderRadius: '5px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
    zIndex: 1000,
    color: 'white',
    maxHeight: '600px', // Increased height to accommodate more models
    overflowY: 'scroll',
    width: '300px', // Fixed width for better layout
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)', // 2 columns
    gap: '10px',
  },
  thumbnail: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    background: 'rgba(255, 255, 255, 0.1)',
    borderRadius: '5px',
    padding: '10px',
    height: '150px', // Fixed height for thumbnails
    position: 'relative', // For positioning loader
    transition: 'background-color 0.3s ease', // Smooth transition for background color
  },
  loaderContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
  },
  modelCanvas: {
    width: '100%', // Full width of the container
    height: '100%', // Full height of the container
  },
};


const Toolbox = ({
  handleModelUpload,
  toggleDrawingMode,
  isDrawing,
  toggleTransformMode,
  transformMode,
  toggleRotateMode,
  rotateMode,
  exportModel,
  adaptDenture,
  toggleTorchMode,
  isTorchMode,
  handleIntersect,
  toggleMergeMode,
  isMergeMode
}) => {
  const handleAdaptDenture = async () => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: "Do you want to adapt the denture to the drawn lines?",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, adapt it!',
      cancelButtonText: 'No, cancel!',
    });

    if (result.isConfirmed) {
      adaptDenture(); // Call adaptDenture only if confirmed
    }
  };

  return (
    <div className="toolbox p-2" style={{ backgroundColor: '#444444' }}> {/* Dark background */}
      {/* Logo instead of title */}
      <div className="text-center mb-3">
        <img
          src="https://www.alienmilling.com/cdn/shop/t/31/assets/logo.png"
          alt="Business Logo"
          style={{ maxWidth: '100%', height: 'auto', maxHeight: '50px' }} // Adjust as needed
        />
      </div>

      {/* Row 1: Import Model */}
      <div className="text-center mb-3">
        <button
          className="btn btn-secondary"
          data-bs-toggle="tooltip"
          data-bs-placement="top"
          title="Import Model"
          onClick={() => document.getElementById('file-input').click()}
        >
          <i className="fa fa-upload"></i> {/* Import Icon */}
          <span className="ms-1">Import Model</span> {/* Add a small margin to the text */}
        </button>
        <input
          id="file-input"
          type="file"
          accept=".stl"
          onChange={handleModelUpload}
          style={{ display: 'none' }}
        />
      </div>

      {/* Row 2: Drawing and Transform Modes */}
      <div className="d-flex justify-content-between mb-3">
        <button
          onClick={toggleDrawingMode}
          className={`btn ${isDrawing ? 'btn-danger' : 'btn-secondary'}`}
          data-bs-toggle="tooltip"
          data-bs-placement="top"
          title="Enable Drawing Mode"
        >
          <div className="d-flex flex-column align-items-center">
            <i className={`fa ${isDrawing ? 'fa-stop' : 'fa-pencil'}`}></i>
            <small className="mt-1 text-white">Draw</small>
          </div>
        </button>

        <button
          onClick={exportModel}
          className="btn btn-secondary"
          data-bs-toggle="tooltip"
          data-bs-placement="top"
          title="Export Model"
        >
          <div className="d-flex flex-column align-items-center">
            <i className="fa fa-download"></i>
            <small className="mt-1 text-white">Export</small>
          </div>
        </button>


      </div>

      <div className="d-flex justify-content-between mb-3">
        <button
          onClick={toggleTransformMode}
          className={`btn ${transformMode ? 'btn-danger' : 'btn-secondary'}`}
          data-bs-toggle="tooltip"
          data-bs-placement="top"
          title="Enable Transform Mode"
        >
          <div className="d-flex flex-column align-items-center">
            <i className={`fa ${transformMode ? 'fa-stop' : 'fa-arrows'}`}></i>
            <small className="mt-1 text-white">Transform</small>
          </div>
        </button>


        <button
          onClick={toggleRotateMode}
          className={`btn ${rotateMode ? 'btn-danger' : 'btn-secondary'}`}
          data-bs-toggle="tooltip"
          data-bs-placement="top"
          title="Enable Rotate Mode"
        >
          <div className="d-flex flex-column align-items-center">
            <i className={`fa ${rotateMode ? 'fa-stop' : 'fa-repeat'}`}></i>
            <small className="mt-1 text-white">Rotate</small>
          </div>
        </button>

      </div>

      {/* Row 3: Export and Adapt Denture */}
      <div className="d-flex justify-content-between mb-3">


        <button
          onClick={handleAdaptDenture}
          className="btn btn-secondary"
          data-bs-toggle="tooltip"
          data-bs-placement="top"
          title="Adapt Denture"
        >
          <div className="d-flex flex-column align-items-center">
            <i className="fa fa-wrench"></i>
            <small className="mt-1 text-white">Adapt</small>
          </div>
        </button>


        <button
          onClick={handleIntersect}  // Call the handleIntersect function
          className="btn btn-secondary"
          data-bs-toggle="tooltip"
          data-bs-placement="top"
          title="Check Intersection"
        >
          <div className="d-flex flex-column align-items-center">
            <i className="fa fa-compress"></i>
            <small className="mt-1 text-white">Intersect</small>
          </div>
        </button>

      </div>

      <div className="d-flex justify-content-center">



        <button
          onClick={toggleMergeMode}  // Toggle between merging and unmerging
          className={`btn ${isMergeMode ? 'btn-danger' : 'btn-secondary'}`}
          data-bs-toggle="tooltip"
          data-bs-placement="top"
          title={isMergeMode ? "Unmerge Models" : "Merge Models"}
        >
          <div className="d-flex flex-column align-items-center">
            <i className={`fa ${isMergeMode ? 'fa-object-ungroup' : 'fa-object-group'}`}></i>
            <small className="mt-1 text-white">{isMergeMode ? "Unmerge" : "Merge"}</small>
          </div>
        </button>

        <button
          onClick={toggleTorchMode}
          className={`btn ${isTorchMode ? 'btn-danger' : 'btn-secondary'}`}
          data-bs-toggle="tooltip"
          data-bs-placement="top"
          title="Enable Torch Mode"
        >
          <div className="d-flex flex-column align-items-center">
            <i className={`fa ${isTorchMode ? 'fa-stop' : 'fa-lightbulb-o'}`}></i>
            <small className="mt-1 text-white">Torch</small>
          </div>
        </button>


      </div>

      {/* Intersect Button */}
      <div className="d-flex justify-content-center mb-3">

      </div>

    </div>
  );
};

const Footer = () => {
  return (
    <footer className="text-light p-3" style={{ position: 'absolute', bottom: 0, width: '100%', backgroundColor: '#444444', }}>
      <div className="container">
        <div className="card text-light" style={{ backgroundColor: '#444444', boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)' }}>
          <div className="card-body">
            <h5 className="card-title">
              <i className="fa fa-info-circle me-2"></i> Guidelines
            </h5>
            <ul className="list-unstyled card-text">
              <li className="mb-2">
                <i className="fa fa-hand-pointer-o me-2"></i>
                <kbd>Shift</kbd> + <kbd>Mouse Down</kbd> + <kbd>Mouse Move</kbd> : <span> Pan the camera around the model for a detailed inspection </span>
              </li>
              <li className="mb-2">
                <i className="fa fa-mouse-pointer me-2"></i>
                <kbd>Alt</kbd> + <kbd>Mouse Click</kbd> : <span> Draw lines directly on the model</span>
              </li>
              <li className="mb-2">
                <i className="fa fa-wrench me-2"></i>
                <span>Use the toolbar to import, transform, or merge models.</span>
              </li>
            </ul>

          </div>
        </div>
      </div>
    </footer>
  );
};


// Add dynamic loading spinner code here
const loadingMessages = [
  "Cutting your denture model...",
  "Almost there...",
  "Optimizing model...",
  "Applying transformations...",
  "Rendering your view...",
  "Aligning models...",
  "Preparing 3D scene...",
  "Setting up your tools...",
  "Loading assets...",
  "Processing data...",
  "Merging geometries...",
  "Checking intersections...",
  "Finalizing adjustments...",
  "Double-checking measurements...",
  "Applying final touches...",
  "Calibrating tools...",
  "Fine-tuning details...",
  "Smoothing edges...",
  "Organizing layers...",
  "Analyzing structure...",
  "Preparing 3D mesh...",
  "Scanning for improvements...",
  "Confirming settings...",
  "Customizing view...",
  "Loading configurations...",
  "Synchronizing components...",
  "Gathering resources...",
  "Applying finishing touches...",
  "Getting things ready...",
  "Cleaning up the workspace...",
  "Optimizing for precision...",
  "Almost ready for export...",
  "Making everything perfect...",
  "Crafting final shape...",
  "Fine-tuning parameters...",
  "Double-checking everything...",
  "Polishing model surface...",
  "Adjusting for best fit...",
  "Ready for final review...",
  "Finalizing precision cuts...",
  "Prepping for export...",
  "Making final adjustments...",
];


const LoadingSpinner = () => {
  const [message, setMessage] = useState("");

  useEffect(() => {
    // Function to set a random message
    const updateMessage = () => {
      const randomMessage = loadingMessages[Math.floor(Math.random() * loadingMessages.length)];
      setMessage(randomMessage);
    };

    updateMessage(); // Set initial message

    // Function to set a random interval between updates (1-3 seconds)
    const setRandomInterval = () => {
      return setInterval(updateMessage, Math.floor(Math.random() * 2000) + 1000); // 1000 to 3000 ms
    };

    let intervalId = setRandomInterval(); // Set initial interval

    // Clear and reset interval on each update to change the timing randomly
    const intervalHandler = setInterval(() => {
      clearInterval(intervalId);
      intervalId = setRandomInterval();
    }, 3000); // Check every 3 seconds to reset the interval

    return () => {
      clearInterval(intervalId);
      clearInterval(intervalHandler); // Clear on component unmount
    };
  }, []);

  return (
    <div style={{
      position: 'fixed',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      color: 'white',
      fontSize: '1.5em',
      zIndex: 1000,
      background: 'rgba(0, 0, 0, 0.5)',
      padding: '20px',
      borderRadius: '10px',
      width: '100%',
      height: '100%',
      textAlign: 'center',
      alignContent: 'center'
    }}>
      <i className="fa fa-spinner fa-spin"></i> {message}
    </div>
  );
};


const App = () => {
  const mountRef = useRef(null);
  const [model, setModel] = useState(null);
  const [points, setPoints] = useState([]);
  const [isDrawing, setIsDrawing] = useState(false);
  const [transformMode, setTransformMode] = useState(false);
  const [rotateMode, setRotateMode] = useState(false);
  const sceneRef = useRef();
  const cameraRef = useRef();
  const rendererRef = useRef();
  const orbitControlsRef = useRef();
  const transformControlsRef = useRef();
  const boundingBoxRef = useRef();
  const [isTorchMode, setIsTorchMode] = useState(false); // Track torch mode
  const [isMergeMode, setIsMergeMode] = useState(false);
  const pointLightRef = useRef(); // Create a ref for the point light
  const [selectedModel, setSelectedModel] = useState(null);
  const [dentureModels, setDentureModels] = useState([]); // Store denture models (Layer B)
  const brushRaycasterRef = useRef(new THREE.Raycaster());
  const brushIntersectionRef = useRef([]);
  const brushSize = 5; // Adjust brush size if necessary
  const boundaryLineRef = useRef(null);
  const [clippedDentureModel, setClippedDentureModel] = useState(null); // Store the clipped denture model
  const [mergedModel, setMergedModel] = useState(null); // Store merged model
  const [originalModels, setOriginalModels] = useState([]); // Store original individual models
  const [mouseIsPressed, setMouseIsPressed] = useState(false);
  const [isAltPressed, setIsAltPressed] = useState(false);
  const [loading, setLoading] = useState(false); // Loading state for API call

  useEffect(() => {
    // Initialize scene
    const scene = new THREE.Scene();
    scene.background = new THREE.Color("#444444");
    sceneRef.current = scene;

    const camera = new THREE.PerspectiveCamera(75, window.innerWidth / window.innerHeight, 0.1, 1000);
    camera.position.set(0, 60, 70);
    cameraRef.current = camera;
    camera.layers.enable(1);
    camera.layers.enable(2);
    camera.layers.enable(3);

    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setSize(window.innerWidth, window.innerHeight);
    mountRef.current.appendChild(renderer.domElement);
    rendererRef.current = renderer;

    const orbitControls = new OrbitControls(camera, renderer.domElement);
    orbitControls.enableDamping = true;
    orbitControls.dampingFactor = 0.1;
    orbitControls.screenSpacePanning = false;
    orbitControls.maxPolarAngle = Math.PI;
    orbitControls.minDistance = 5;
    orbitControls.maxDistance = 135;
    orbitControlsRef.current = orbitControls;

    const transformControls = new TransformControls(camera, renderer.domElement);
    scene.add(transformControls);
    transformControlsRef.current = transformControls;



    // Create point light and store it in the ref
    const pointLight = new THREE.PointLight(0xffff00, 10, 50); // Increased intensity to 5 and distance to 100
    pointLight.decay = 1; // Adjust decay
    pointLight.position.set(0, 5, 0);
    pointLight.castShadow = false; // Ensure the light does not cast shadows
    scene.add(pointLight);
    pointLightRef.current = pointLight; // Save point light reference

    // Optional: Add a light helper to visualize the light, but keep it invisible
    const lightHelper = new THREE.PointLightHelper(pointLight, 1); // 1 is the size of the helper
    scene.add(lightHelper);
    lightHelper.material.visible = false; // Set helper material to invisible

    // Ensure the model uses a material that does not respond to lighting
    if (model) {
      model.traverse((child) => {
        if (child.isMesh) {
          child.material = new THREE.MeshBasicMaterial({ color: 0xaaaaaa }); // Example color
        }
      });
    }

    // Create a small sphere to represent the bulb visually (but make it invisible)
    const bulbGeometry = new THREE.SphereGeometry(0.1, 8, 8);
    const bulbMaterial = new THREE.MeshBasicMaterial({ color: 0xffff00, transparent: true, opacity: 0 }); // Set opacity to 0
    const bulb = new THREE.Mesh(bulbGeometry, bulbMaterial);
    scene.add(bulb);

    // Update the bulb position to match the light position
    const updateBulbPosition = () => {
      bulb.position.copy(pointLight.position);
    };

    /*const light = new THREE.DirectionalLight(0xffffff, 1);
    light.position.set(0, 10, 10).normalize();
    scene.add(light);*/

    // Add ambient light
    /*const ambientLight = new THREE.AmbientLight(0xffffff, 0.5); // Soft light
    scene.add(ambientLight);

    // Create a hemisphere light to simulate sunlight coming from above
    const hemisphereLight = new THREE.HemisphereLight(0xffffff, 0x000000, 0.5);
    scene.add(hemisphereLight);*/


    // Create multiple directional lights to illuminate from different angles
    const createDirectionalLight = (x, y, z) => {
      const light = new THREE.DirectionalLight(0xffffff, 0.5);
      light.position.set(x, y, z).normalize();
      scene.add(light);
    };

    // Add directional lights
    createDirectionalLight(1, 1, 1); // Front-right
    createDirectionalLight(-1, 1, 1); // Front-left
    createDirectionalLight(1, 1, -1); // Back-right
    createDirectionalLight(-1, 1, -1); // Back-left
    createDirectionalLight(0, -1, 0); // Bottom
    createDirectionalLight(0, 1, 0); // Top
    createDirectionalLight(0, 0, 1); // Front
    createDirectionalLight(0, 0, -1); // Back
    createDirectionalLight(1, 0, 0); // Right
    createDirectionalLight(-1, 0, 0); // Left

    const gridColor = 0xffffff; // Custom color for the grid lines (green)
    const centerLineColor = 0xffffff; // Color for the center line (black)

    const gridHelper = new THREE.GridHelper(100, 100, centerLineColor, gridColor);
    gridHelper.name = "Grid Helper";  // Set a name for the model
    scene.add(gridHelper);
    // gridHelper.material.visible = false; // Set helper material to invisible

    const animate = () => {
      requestAnimationFrame(animate);
      renderer.render(scene, camera);
      orbitControls.update();

      if (transformMode && transformControlsRef.current) {
        transformControlsRef.current.update();
      }

      if (rotateMode && transformControlsRef.current) {
        transformControlsRef.current.update();
      }

      updateBulbPosition(); // Keep the bulb in sync with the light
    };
    animate();

    const handleResize = () => {
      camera.aspect = window.innerWidth / window.innerHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(window.innerWidth, window.innerHeight);
    };

    window.addEventListener('resize', handleResize);
    return () => {
      mountRef.current.removeChild(renderer.domElement);
      window.removeEventListener('resize', handleResize);
    };
  }, []);


  const handleModelSelected = (selectedModel, isDeselected) => {

    if (!isDeselected) {
      // Deselecting: Remove all models in Layer B (previously selected models)
      dentureModels.forEach((model) => {
        // Remove the model itself
        sceneRef.current.remove(model);

        // Detach the TransformControls from the model (if it's attached)
        if (transformControlsRef.current && transformControlsRef.current.object === model) {
          transformControlsRef.current.detach();  // Detach the transform controls from the object
        }

        // Remove any associated bounding boxes or helpers tied to the model
        if (model.boundingBoxHelper) {
          sceneRef.current.remove(model.boundingBoxHelper);  // Assuming bounding boxes are added as helpers
        }

        // Optionally dispose of the model's geometry and material to free up memory
        if (model.geometry) model.geometry.dispose();
        if (model.material) model.material.dispose();
      });

      // Clear the dentureModels state (remove all models from Layer B)
      setDentureModels([]);


      // Remove any UI elements like buttons related to this specific model
      const buttonContainer = document.querySelector('.ui-model-buttons');  // Select the element with class "ui-model-buttons"
      if (buttonContainer) {
        buttonContainer.remove();  // Remove button container only related to this model
      }
    } else {
      // Selecting: Now add the newly selected model to the scene
      if (selectedModel) {

        // Remove all previous models and clean up before adding the new model
        dentureModels.forEach((model) => {
          sceneRef.current.remove(model);

          // Detach the TransformControls from the model (if it's attached)
          if (transformControlsRef.current && transformControlsRef.current.object === model) {
            transformControlsRef.current.detach();  // Detach the transform controls from the object
          }

          if (model.boundingBoxHelper) {
            sceneRef.current.remove(model.boundingBoxHelper);  // Remove bounding boxes or helpers
          }
          if (model.geometry) model.geometry.dispose();
          if (model.material) model.material.dispose();
        });
        setDentureModels([]);  // Clear state


        // Apply visual adjustments to the selected model
        const geometry = selectedModel.geometry;  // Assuming `selectedModel` is a mesh with geometry
        const material = new THREE.MeshStandardMaterial({
          color: 0xffff00,  // Example color for better visibility
          metalness: 0.5,
          roughness: 0.5,
          side: THREE.DoubleSide  // Ensure both sides are rendered
        });

        // Create a new mesh with the updated material
        const mesh = new THREE.Mesh(geometry, material);
        mesh.name = "Adapt Denture Object";  // Set a name for the model
        mesh.tags = "Layer B";  // Assign an array of custom tags (or metadata)
        // Compute the bounding box of the model
        geometry.computeBoundingBox();
        const boundingBox = geometry.boundingBox;

        // Get the center of the bounding box and center the model
        const center = new THREE.Vector3();
        boundingBox.getCenter(center);
        mesh.position.sub(center);  // Center the model
        // If there's an existing model in the state, get the center of that model and move the new mesh to it
        if (model) {
          // Compute the bounding box for the existing model
          const modelBoundingBox = new THREE.Box3().setFromObject(model);

          // Get the center point of the existing model
          const modelCenter = new THREE.Vector3();
          modelBoundingBox.getCenter(modelCenter);

          // Move the new mesh to the center point of the existing model
          mesh.position.set(modelCenter.x, modelCenter.y, modelCenter.z);
        }

        // Adjust the Y position to place the model correctly
        const halfHeight = boundingBox.max.y - boundingBox.min.y;
        // mesh.position.y += (halfHeight / 2) + 1;  // Adjust the Y position
        mesh.position.y += 10;


        // Scale the model down uniformly (e.g., reduce to 50% of original size)
        const scaleFactor = 0.3;  // Adjust this value to control the size of the model
        // mesh.scale.set(scaleFactor, scaleFactor, scaleFactor);  // Apply uniform scaling

        // Assign this model to Layer 2 (Layer B)
        mesh.layers.set(2);

        attachTransformControls(mesh);
        // Add the mesh to the scene
        sceneRef.current.add(mesh);

        mesh.rotation.x += Math.PI / 2;  // Rotate by 180 degrees
        mesh.updateMatrixWorld(true);

        // Update the dentureModels state to include only the newly selected model
        setDentureModels([mesh]);
      }
    }
  };


  const handleModelUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const contents = e.target.result;
        loadModel(contents);
      };
      reader.readAsArrayBuffer(file);
    }
  };

  const loadModel = (data) => {
    const loader = new STLLoader();
    const geometry = loader.parse(data);
    // Change the material to a more visible one
    const material = new THREE.MeshStandardMaterial({
      color: 0xd4667f,
      metalness: 0.5,
      roughness: 0.5,
      side: THREE.DoubleSide // Ensure both sides are rendered
    });
    const mesh = new THREE.Mesh(geometry, material);
    mesh.name = "Dental Object";  // Set a name for the model
    mesh.tags = "Layer A";  // Assign an array of custom tags (or metadata)


    geometry.computeBoundingBox();
    const boundingBox = geometry.boundingBox;

    const center = new THREE.Vector3();
    boundingBox.getCenter(center);

    mesh.position.sub(center);

    // Calculate half the height of the bounding box
    const halfHeight = boundingBox.max.y - boundingBox.min.y;

    // Set the y position of the mesh to half the height of the bounding box
    mesh.position.y += (halfHeight / 2) + 1;
    mesh.layers.set(1);
    // mesh.layers.set(1);  // Assign this model to Layer 1 (Layer A)
    sceneRef.current.add(mesh);
    setModel(mesh);

    createBoundingBox(boundingBox, mesh);

    transformControlsRef.current.setTranslationSnap(1);
    transformControlsRef.current.setRotationSnap(THREE.MathUtils.degToRad(15));
  };

  const createBoundingBox = (boundingBox, mesh) => {
    if (boundingBoxRef.current) {
      sceneRef.current.remove(boundingBoxRef.current);
    }

    const boxHelper = new THREE.Box3Helper(boundingBox, /* 0xff0000*/ 0x359C25);
    boxHelper.layers.set(3); // Assign the bounding box to layer 3
    sceneRef.current.add(boxHelper);
    boundingBoxRef.current = boxHelper;
    mesh.add(boxHelper);
  };

  const createVertexDot = (position) => {
    const geometry = new THREE.SphereGeometry(0.2, 32, 32);
    const material = new THREE.MeshBasicMaterial({ color: 0x51F234 });
    const dot = new THREE.Mesh(geometry, material);
    dot.position.copy(position);

    dot.name = "DrawingPoint"; // Assign a name for identification


    if (dentureModels.length == 2) {
      const dentureModel = dentureModels[0]; // Target the first denture model or modify this based on selection

      if (dentureModel) {
        dentureModel.add(dot);
      }

    } else {
      if (model) {
        model.add(dot);
      }

    }


    sceneRef.current.add(dot);
  };

  const drawLines2 = (points) => {
    const existingLines = sceneRef.current.getObjectByName('DrawingLine');
    if (existingLines) {
      sceneRef.current.remove(existingLines);
    }

    const lineGeometry = new THREE.BufferGeometry().setFromPoints(points);
    const lineMaterial = new THREE.LineBasicMaterial({ color: 0x51F234 });
    const lines = new THREE.Line(lineGeometry, lineMaterial);
    lines.name = 'DrawingLine';

    if (model) {
      model.add(lines);
    }

    sceneRef.current.add(lines);
  };


  const drawLines = (points) => {
    // Check if the points array is valid
    if (!points || points.length < 2) {
      console.error("Insufficient points to draw lines. The points array must contain at least two elements.");
      return;
    }

    // Check if all elements in the points array are valid THREE.Vector3 objects
    const arePointsValid = points.every(point => point instanceof THREE.Vector3);
    if (!arePointsValid) {
      console.error("One or more points are invalid. Ensure all elements are THREE.Vector3 instances.");
      return;
    }

    // Remove existing line if it exists
    const existingLines = sceneRef.current.getObjectByName('DrawingLine');
    if (existingLines) {
      sceneRef.current.remove(existingLines);
      if (existingLines.geometry) existingLines.geometry.dispose(); // Dispose of geometry
      if (existingLines.material) existingLines.material.dispose(); // Dispose of material
    }

    // Create a CatmullRomCurve3 curve using the points array
    try {
      const curve = new THREE.CatmullRomCurve3(points); // Create a smooth curve through the points

      // Create a tube geometry from the curve
      const tubeGeometry = new THREE.TubeGeometry(curve, 64, 0.05, 8, false); // Thickness defined by radius (0.05)

      // Create a material
      const lineMaterial = new THREE.MeshBasicMaterial({ color: 0x51F234 });

      // Create the line mesh (tube)
      const thickLine = new THREE.Mesh(tubeGeometry, lineMaterial);
      thickLine.name = 'DrawingLine';

      if (dentureModels.length == 2) {
        const dentureModel = dentureModels[0]; // Target the first denture model or modify this based on selection

        if (dentureModel) {
          dentureModel.add(thickLine);
        }

      } else {
        if (model) {
          model.add(thickLine);
        }

      }

      sceneRef.current.add(thickLine);
    } catch (error) {
      console.error("Failed to create tube geometry:", error);
    }
  };


  const handleMouseMove = (event) => {
    if (isTorchMode && model) {

      // Check if the model has the tag "Layer A"
      if (!model.tags || !model.tags.includes("Layer A")) {
        Swal.fire("Warning", "Torch mode can only be applied to models in Layer A.", "warning");
        return;
      }


      const rect = mountRef.current.getBoundingClientRect();
      const mouse = new THREE.Vector2();
      mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
      mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

      const raycaster = new THREE.Raycaster();
      raycaster.setFromCamera(mouse, cameraRef.current);

      const intersects = raycaster.intersectObject(model); // Use the model as the intersection target
      if (intersects.length > 0) {
        const point = intersects[0].point;
        // Move the light to the intersection point
        pointLightRef.current.position.copy(point);

        // Calculate the distance between the light and the intersection point
        const distance = point.distanceTo(pointLightRef.current.position);

        // Update light intensity based on distance
        // You can adjust the multiplier to get the desired effect
        const newIntensity = Math.max(10 - distance, 0); // Ensure intensity does not go negative
        pointLightRef.current.intensity = newIntensity; // Set the new intensity
      }
    }

  };

  const toggleTorchMode = () => {

    setIsTorchMode((prev) => !prev); // Toggle torch mode
  };


  const toggleDrawingMode = async () => {
    if (!model) {
      await Swal.fire("Warning", "Please upload a model first to enter drawing mode.", "warning");
      return;
    }
    // Check if the model has the tag "Layer A"
    if (!model.tags || !model.tags.includes("Layer A")) {
      await Swal.fire("Warning", "Drawing mode can only be applied to models in Layer A.", "warning");
      return;
    }

    setIsDrawing((prev) => {
      const newMode = !prev;

      if (newMode) {
        orbitControlsRef.current.enabled = true;
        transformControlsRef.current.detach();
        transformControlsRef.current.visible = false;
      } else {
        orbitControlsRef.current.enabled = true;
        if (transformMode) {
          transformControlsRef.current.attach(model);
          transformControlsRef.current.visible = true; // Show transform controls
        }
      }
      return newMode;
    });
  };

  const toggleTransformMode = async () => {
    if (!model) {
      await Swal.fire("Warning", "Please upload a model first before entering transform mode.", "warning");
      return;
    }


    // Check if the model has the tag "Layer A"
    if (!model.tags || !model.tags.includes("Layer A")) {
      await Swal.fire("Warning", "Transform mode can only be applied to models in Layer A.", "warning");
      return;
    }

    // Create a new value based on the current transform mode state
    const newMode = !transformMode;

    // First, handle drawing mode if necessary
    if (newMode && isDrawing) {
      setIsDrawing(false);
      orbitControlsRef.current.enabled = true;
    }

    // Set transform controls visibility and attachment based on the new mode
    if (newMode) {
      transformControlsRef.current.setMode('translate'); // Set to rotate mode explicitly
      transformControlsRef.current.attach(model);
      transformControlsRef.current.visible = true;
      orbitControlsRef.current.enabled = false; // Disable orbit controls in transform mode
    } else {
      transformControlsRef.current.detach();
      transformControlsRef.current.visible = false; // Hide transform controls
      orbitControlsRef.current.enabled = true; // Enable orbit controls
    }

    // Finally, update the transform mode state
    setRotateMode(false);
    setTransformMode(newMode);
  };


  const toggleRotateMode = async () => {
    if (!model) {
      await Swal.fire("Warning", "Please upload a model first before entering rotate mode.", "warning");
      return;
    }


    // Check if the model has the tag "Layer A"
    if (!model.tags || !model.tags.includes("Layer A")) {
      await Swal.fire("Warning", "Rotate mode can only be applied to models in Layer A.", "warning");
      return;
    }


    // Create a new value based on the current rotate mode state
    const newMode = !rotateMode;
    // First, handle drawing mode if necessary
    if (newMode && isDrawing) {
      setIsDrawing(false);
      orbitControlsRef.current.enabled = true;
    }

    // Set rotate controls visibility and attachment based on the new mode
    if (newMode) {
      transformControlsRef.current.setMode('rotate'); // Set to rotate mode explicitly
      transformControlsRef.current.attach(model);
      transformControlsRef.current.visible = true;
      orbitControlsRef.current.enabled = false; // Disable orbit controls in rotate mode
    } else {
      transformControlsRef.current.detach();
      transformControlsRef.current.visible = false; // Hide rotate controls
      orbitControlsRef.current.enabled = true; // Enable orbit controls
    }

    // Finally, update the rotate mode state
    setRotateMode(newMode);
    setTransformMode(false);
  };


  const exportModel = () => {
    if (!model) {
      Swal.fire("Warning", "Please upload a model first before exporting.", "warning");
      return;
    }

    const exporter = new STLExporter();
    const result = exporter.parse(model);

    const blob = new Blob([result], { type: 'application/octet-stream' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'model.stl';
    link.click();
  };


  const handleMouseClick = (event) => {

    if (isDrawing && isAltPressed && model) {

      if (dentureModels.length == 2) {
        const dentureModel = dentureModels[0]; // Target the first denture model or modify this based on selection


        const rect = mountRef.current.getBoundingClientRect();
        const mouse = new THREE.Vector2();
        mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
        mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

        const raycaster = new THREE.Raycaster();
        raycaster.setFromCamera(mouse, cameraRef.current);
        raycaster.layers.set(2); // Set raycaster to target objects on layer 1 only
        const intersects = raycaster.intersectObject(dentureModel);

        if (intersects.length > 0) {
          const point = intersects[0].point;
          createVertexDot(point);
          setPoints((prev) => [...prev, point]);
          drawLines([...points, point]);
        }
      } else {

        // Check if the model has the tag "Layer A"
        if (!model.tags || !model.tags.includes("Layer A")) {
          Swal.fire("Warning", "Drawing mode can only be applied to models in Layer A.", "warning");
          return;
        }

        const rect = mountRef.current.getBoundingClientRect();
        const mouse = new THREE.Vector2();
        mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
        mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

        const raycaster = new THREE.Raycaster();
        raycaster.setFromCamera(mouse, cameraRef.current);
        raycaster.layers.set(1); // Set raycaster to target objects on layer 1 only
        const intersects = raycaster.intersectObject(model);

        if (intersects.length > 0) {
          const point = intersects[0].point;
          createVertexDot(point);
          setPoints((prev) => [...prev, point]);
          drawLines([...points, point]);
        }
      }
    }
  };

  const adaptDenture = async () => {
    if (!points || points.length < 3 || !dentureModels.length) {
      await Swal.fire("Warning", "Please ensure a denture model is selected and enough points are drawn to define the surface.", "warning");
      return;
    }

    const scene = sceneRef.current;  // Assuming you have a scene reference
    const dentureModel = dentureModels[0];  // Assuming there's one denture model at a time

    const materialBoundary = new THREE.LineBasicMaterial({ color: 0xff0000 });  // Red material for boundary around points
    const materialDentureBox = new THREE.LineBasicMaterial({ color: 0xff0000 });  // Green for denture boundary
    const materialCrossLine = new THREE.LineBasicMaterial({ color: 0xff0000 });  // Yellow for cross lines (for center)
    // Calculate the bounding box of the points (this is the red boundary)
    const lineBoundingBox = new THREE.Box3().setFromPoints(points.map(p => new THREE.Vector3(p.x, p.y, p.z)));

    // Extract the corners of the bounding box to form a red boundary
    const min = lineBoundingBox.min;
    const max = lineBoundingBox.max;

    // vertical
    const boundaryVerticesForVertical = [
      new THREE.Vector3(min.x, min.y, min.z),
      new THREE.Vector3(min.x, max.y, min.z),
      new THREE.Vector3(max.x, max.y, min.z),
      new THREE.Vector3(max.x, min.y, min.z),
      new THREE.Vector3(min.x, min.y, min.z)  // Close the loop
    ];


    // horizontal
    const boundaryVerticesForHorizontal = [
      new THREE.Vector3(min.x, min.y, min.z),
      new THREE.Vector3(min.x, min.y, max.z),
      new THREE.Vector3(max.x, min.y, max.z),
      new THREE.Vector3(max.x, min.y, min.z),
      new THREE.Vector3(min.x, min.y, min.z)  // Close the loop
    ];


    // Create the red boundary from the bounding box corners
    const boundaryGeometry = new THREE.BufferGeometry().setFromPoints(boundaryVerticesForHorizontal);
    const boundaryLine = new THREE.Line(boundaryGeometry, materialBoundary);

    boundaryLine.name = "Points Boundary Line";  // Assign a name to the boundary line

    scene.add(boundaryLine);  // Add the red boundary around your points to the scene

    boundaryLineRef.current = boundaryLine;

    // --- Add Cross Lines to the Red Boundary (to visualize the center) ---
    const lineBoundaryCenter = lineBoundingBox.getCenter(new THREE.Vector3());  // Center of the red boundary

    // vertical
    const redCrossVerticesForVertical = [
      // Horizontal cross line
      new THREE.Vector3(min.x, lineBoundaryCenter.y, min.z),
      new THREE.Vector3(max.x, lineBoundaryCenter.y, min.z),
      // Vertical cross line
      new THREE.Vector3(lineBoundaryCenter.x, min.y, min.z),
      new THREE.Vector3(lineBoundaryCenter.x, max.y, min.z),
    ];

    // horizontal
    const redCrossVerticesForHorizontal = [
      // Horizontal cross line (in the X-Z plane)
      new THREE.Vector3(min.x, min.y, lineBoundaryCenter.z),  // Left side of the horizontal cross
      new THREE.Vector3(max.x, min.y, lineBoundaryCenter.z),  // Right side of the horizontal cross
      // Vertical cross line (in the X-Z plane)
      new THREE.Vector3(lineBoundaryCenter.x, min.y, min.z),  // Bottom side of the vertical cross
      new THREE.Vector3(lineBoundaryCenter.x, min.y, max.z),  // Top side of the vertical cross
    ];


    const redCrossGeometry = new THREE.BufferGeometry().setFromPoints(redCrossVerticesForHorizontal);
    const redCrossLine = new THREE.LineSegments(redCrossGeometry, materialCrossLine);

    redCrossLine.name = "Points Cross Boundary Line";  // Assign a name to the boundary line


    scene.add(redCrossLine);  // Add the cross lines for the red boundary


    //  boundaryLine.visible = false;
    // redCrossLine.visible = false;


    // --- Step 2: Draw the green boundary around the denture model and place the denture at the center ---
    const dentureBoundingBox = new THREE.Box3().setFromObject(dentureModel);  // Bounding box for the denture model
    const dentureMin = dentureBoundingBox.min;
    const dentureMax = dentureBoundingBox.max;


    // Create the green bounding box around the denture model
    const dentureBoxVerticesForVertical = [
      new THREE.Vector3(dentureMin.x, dentureMin.y, dentureMin.z),
      new THREE.Vector3(dentureMin.x, dentureMax.y, dentureMin.z),
      new THREE.Vector3(dentureMax.x, dentureMax.y, dentureMin.z),
      new THREE.Vector3(dentureMax.x, dentureMin.y, dentureMin.z),
      new THREE.Vector3(dentureMin.x, dentureMin.y, dentureMin.z)  // Close the loop
    ];


    // Create the green bounding box around the denture model in the X-Z plane (horizontal)
    const dentureBoxVerticesForHorizontal = [
      new THREE.Vector3(dentureMin.x, dentureMin.y, dentureMin.z),  // Bottom-left corner in X-Z plane
      new THREE.Vector3(dentureMin.x, dentureMin.y, dentureMax.z),  // Top-left corner in X-Z plane
      new THREE.Vector3(dentureMax.x, dentureMin.y, dentureMax.z),  // Top-right corner in X-Z plane
      new THREE.Vector3(dentureMax.x, dentureMin.y, dentureMin.z),  // Bottom-right corner in X-Z plane
      new THREE.Vector3(dentureMin.x, dentureMin.y, dentureMin.z)   // Close the loop (back to bottom-left)
    ];



    const dentureBoxGeometry = new THREE.BufferGeometry().setFromPoints(dentureBoxVerticesForHorizontal);
    const dentureBoxLine = new THREE.Line(dentureBoxGeometry, materialDentureBox);

    dentureBoxLine.name = "Denture Boundary Line";  // Assign a name to the boundary line


    scene.add(dentureBoxLine);  // Add the denture model's boundary box to the scene

    // --- Add Cross Lines to the Green Boundary (Denture Model) ---
    const dentureBoundaryCenter = dentureBoundingBox.getCenter(new THREE.Vector3());  // Center of the green boundary

    const greenCrossVerticesForVertical = [
      // Horizontal cross line
      new THREE.Vector3(dentureMin.x, dentureBoundaryCenter.y, dentureMin.z),
      new THREE.Vector3(dentureMax.x, dentureBoundaryCenter.y, dentureMin.z),
      // Vertical cross line
      new THREE.Vector3(dentureBoundaryCenter.x, dentureMin.y, dentureMin.z),
      new THREE.Vector3(dentureBoundaryCenter.x, dentureMax.y, dentureMin.z),
    ];

    const greenCrossVerticesForHorizontal = [
      // Horizontal cross line (in the X-Z plane)
      new THREE.Vector3(dentureMin.x, dentureMin.y, dentureBoundaryCenter.z),  // Left side of the horizontal cross
      new THREE.Vector3(dentureMax.x, dentureMin.y, dentureBoundaryCenter.z),  // Right side of the horizontal cross
      // Vertical cross line (in the X-Z plane)
      new THREE.Vector3(dentureBoundaryCenter.x, dentureMin.y, dentureMin.z),  // Bottom side of the vertical cross
      new THREE.Vector3(dentureBoundaryCenter.x, dentureMin.y, dentureMax.z),  // Top side of the vertical cross
    ];


    const greenCrossGeometry = new THREE.BufferGeometry().setFromPoints(greenCrossVerticesForHorizontal);
    const greenCrossLine = new THREE.LineSegments(greenCrossGeometry, materialCrossLine);

    greenCrossLine.name = "Denture Cross Boundary Line";  // Assign a name to the boundary line


    scene.add(greenCrossLine);  // Add the cross lines for the green boundary


    //  dentureBoxLine.visible = false;
    //  greenCrossLine.visible = false;


    // Calculate the distance between the centers (this will be used to move the denture)
    const centerDistance = new THREE.Vector3().subVectors(lineBoundaryCenter, dentureBoundaryCenter);

    // --- Step 4: Move the denture model, its boundary, and the cross lines to the center of the line boundary ---
    dentureModel.position.add(centerDistance);  // Move the denture model to align with the line boundary's center
    dentureBoxLine.position.add(centerDistance);  // Move the denture boundary box to align with the line boundary center
    greenCrossLine.position.add(centerDistance);  // Move the cross lines of the denture boundary as well


    attachTransformControls(dentureModel);
  };

  const cleanUpAfterAdaptation = () => {

    const dentureModel = dentureModels[0]; // Target the first denture model or modify this based on selection

    // Detach transform controls only if they are attached to the denture model
    if (transformControlsRef.current && transformControlsRef.current.object === dentureModel) {
      if (transformControlsRef.current.detachCleanup) {
        transformControlsRef.current.detachCleanup();  // Call the cleanup function defined earlier
      }
    }

    // Re-enable OrbitControls only if they were disabled due to the denture model
    orbitControlsRef.current.enabled = true;

    // Remove any UI elements like buttons related to this specific model
    const buttonContainer = document.querySelector('.ui-model-buttons');  // Select the element with class "ui-model-buttons"
    if (buttonContainer) {
      buttonContainer.remove();  // Remove button container only related to this model
    }


    console.log("Cleaned up transform controls, buttons, and UI for the denture model after adaptation.");
  };


  const attachTransformControls = (model) => {

    // Remove any UI elements like buttons related to this specific model
    const buttonContainer2 = document.querySelector('.ui-model-buttons');  // Select the element with class "ui-model-buttons"
    if (buttonContainer2) {
      buttonContainer2.remove();  // Remove button container only related to this model
    }


    // Attach the TransformControls to the selected model
    transformControlsRef.current.attach(model);
    sceneRef.current.add(transformControlsRef.current);

    // Create Font Awesome buttons in HTML and position them near the model
    const buttonContainer = document.createElement('div');
    buttonContainer.classList.add('ui-model-buttons');  // Add class "ui-model-buttons"
    buttonContainer.style.position = 'absolute';
    buttonContainer.style.top = '10px'; // This will be adjusted later based on the model's position
    buttonContainer.style.left = '50px'; // Adjust this based on your preference
    buttonContainer.style.display = 'flex';
    buttonContainer.style.gap = '10px'; // Gap between buttons

    // Create Move, Rotate, and Scale buttons using Bootstrap classes
    const moveButton = document.createElement('button');
    moveButton.innerHTML = '<i class="fa fa-arrows"></i>'; // Font Awesome Move Icon
    moveButton.title = 'Move';
    moveButton.className = 'btn btn-secondary'; // Inactive mode (btn-secondary)

    const rotateButton = document.createElement('button');
    rotateButton.innerHTML = '<i class="fa fa-repeat"></i>'; // Font Awesome Rotate Icon
    rotateButton.title = 'Rotate';
    rotateButton.className = 'btn btn-secondary'; // Inactive mode (btn-secondary)

    const scaleButton = document.createElement('button');
    scaleButton.innerHTML = '<i class="fa fa-expand"></i>'; // Font Awesome Scale Icon
    scaleButton.title = 'Scale';
    scaleButton.className = 'btn btn-secondary'; // Inactive mode (btn-secondary)

    // Append buttons to the button container
    buttonContainer.appendChild(moveButton);
    buttonContainer.appendChild(rotateButton);
    buttonContainer.appendChild(scaleButton);

    // Append the button container to the body (or your app container)
    document.body.appendChild(buttonContainer);

    let activeMode = null; // Track the active transformation mode

    // Function to disable all buttons
    const disableAllButtons = () => {
      moveButton.className = 'btn btn-secondary'; // Set to inactive mode
      rotateButton.className = 'btn btn-secondary'; // Set to inactive mode
      scaleButton.className = 'btn btn-secondary'; // Set to inactive mode
      orbitControlsRef.current.enabled = true; // Enable orbit controls
      transformControlsRef.current.detach(); // Detach TransformControls
      activeMode = null;
    };

    // Set button click event listeners
    moveButton.addEventListener('click', () => {
      if (activeMode === 'translate') {
        disableAllButtons(); // If already active, disable it
      } else {
        activateMode('translate', moveButton);
      }
    });

    rotateButton.addEventListener('click', () => {
      if (activeMode === 'rotate') {
        disableAllButtons();
      } else {
        activateMode('rotate', rotateButton);
      }
    });

    scaleButton.addEventListener('click', () => {
      if (activeMode === 'scale') {
        disableAllButtons();
      } else {
        activateMode('scale', scaleButton);
      }
    });

    // Function to activate a transformation mode
    const activateMode = (mode, button) => {
      disableAllButtons(); // Disable other modes
      button.className = 'btn btn-danger'; // Highlight active button (btn-danger)
      transformControlsRef.current.setMode(mode); // Set TransformControls mode
      orbitControlsRef.current.enabled = false; // Disable OrbitControls
      transformControlsRef.current.attach(model); // Attach TransformControls to the model
      activeMode = mode; // Track the active mode
    };

    // Reposition the buttons to be near the model in 3D space
    const repositionButtons = () => {
      const vector = new THREE.Vector3(); // Create a vector to store model position in screen space
      const box = new THREE.Box3().setFromObject(model);
      box.getCenter(vector); // Get the center of the model

      // Project the 3D position into 2D screen space
      vector.project(cameraRef.current);

      const widthHalf = window.innerWidth / 2;
      const heightHalf = window.innerHeight / 2;

      // Update the button container's position based on the model's screen position
      buttonContainer.style.left = `${(vector.x * widthHalf) + widthHalf - 50}px`; // Adjust offset for width
      buttonContainer.style.top = `${-(vector.y * heightHalf) + heightHalf - 50}px`; // Adjust offset for height
    };

    // Reposition buttons on every render to keep them near the model
    transformControlsRef.current.addEventListener('change', repositionButtons);

    // activateMode('translate', moveButton);  // Set translate as the active mode initially

    // Call repositionButtons initially

    repositionButtons();

    // Remove buttons when detaching controls
    const detachTransformControls = () => {
      transformControlsRef.current.detach();
      sceneRef.current.remove(transformControlsRef.current);
      buttonContainer.remove(); // Remove the buttons from the DOM
    };

    transformControlsRef.current.detachCleanup = detachTransformControls; // Store cleanup function
  };

  const handleSubtract2 = async () => {
    console.log("handleSubtract called");

    if (!points || points.length < 3 || !dentureModels.length) {
      console.error("Error: Not enough points or no denture models found.");
      await Swal.fire("Error", "Please ensure a denture model is selected and enough points are drawn to define the boundary.", "error");
      return;
    }

    console.log("Points and denture models are valid");

    const dentureModel = dentureModels[0]; // Target the first denture model or modify this based on selection
    console.log("Denture model selected:", dentureModel);

    // Ensure the denture model is in Layer B
    if (!dentureModel.tags || !dentureModel.tags.includes("Layer B")) {
      console.warn("Warning: Denture model is not in Layer B.");
      await Swal.fire("Warning", "Brush mode can only be applied to models in Layer B.", "warning");
      return;
    }

    console.log("Denture model is in Layer B");

    // Check if boundary line is defined
    if (!boundaryLineRef.current) {
      console.error("Error: Boundary line reference is not found.");
      await Swal.fire("Error", "Boundary line reference not found.", "error");
      return;
    }

    console.log("Boundary line reference found");

    // Extract the points from the boundaryLineRef
    const boundaryLine = boundaryLineRef.current;
    if (!boundaryLine.geometry || !boundaryLine.geometry.attributes || !boundaryLine.geometry.attributes.position) {
      console.error("Error: Boundary line geometry is not properly defined.");
      await Swal.fire("Error", "Boundary line geometry is invalid.", "error");
      return;
    }

    const positions = boundaryLine.geometry.attributes.position.array; // Access the vertex positions
    console.log("Boundary line positions:", positions);

    // Convert positions (which are a flat array of numbers) into an array of THREE.Vector3
    const boundaryPoints = [];
    for (let i = 0; i < positions.length; i += 3) {
      boundaryPoints.push(new THREE.Vector3(positions[i], positions[i + 1], positions[i + 2]));
    }
    console.log("Boundary points extracted:", boundaryPoints);

    // Create a new Shape object using the boundary points
    const shape = new THREE.Shape();
    boundaryPoints.forEach((point, index) => {
      if (index === 0) {
        shape.moveTo(point.x, point.z); // Start from the first point
      } else {
        shape.lineTo(point.x, point.z); // Draw line to next point
      }
    });
    shape.closePath(); // Close the boundary
    console.log("Shape created from boundary points");

    // Create clipping planes from the boundary shape in XZ
    const clippingPlanes = [];
    for (let i = 0; i < boundaryPoints.length; i++) {
      const p1 = boundaryPoints[i];
      const p2 = boundaryPoints[(i + 1) % boundaryPoints.length]; // Loop around to the first point

      // Create a normal vector for each boundary edge
      const edgeDirection = new THREE.Vector3(p2.x - p1.x, 0, p2.z - p1.z);
      const edgeNormal = new THREE.Vector3(-edgeDirection.z, 0, edgeDirection.x).normalize(); // Normal facing outward

      // Create a plane from the edge
      const plane = new THREE.Plane().setFromNormalAndCoplanarPoint(edgeNormal.negate(), p1); // Flip the normal if needed
      clippingPlanes.push(plane);

      // Add plane helper for debugging
      const planeHelper = new THREE.PlaneHelper(plane, 100, 0xff0000); // Visible helper for clipping plane
      sceneRef.current.add(planeHelper); // Add helper to the scene

      // Make the helper invisible
      planeHelper.visible = false;
    }
    console.log("Clipping planes created:", clippingPlanes);

    // Update the denture model material to respect the clipping planes
    if (!dentureModel.material) {
      console.error("Error: Denture model material not found.");
      return;
    }

    dentureModel.material.clippingPlanes = clippingPlanes;
    dentureModel.material.clipShadows = true; // Optionally clip shadows
    dentureModel.material.transparent = true; // Ensure transparency is enabled
    dentureModel.material.opacity = 0.8; // Set opacity to help with visibility
    console.log("Updated denture model material with clipping planes");

    // Enable local clipping in the renderer
    rendererRef.current.localClippingEnabled = true;

    // Store the clipped denture model in state
    setClippedDentureModel(dentureModel);

    // Update the scene with changes
    console.log("Rendering the scene");
    rendererRef.current.render(sceneRef.current, cameraRef.current);
  };


  const handleSubtract3 = async () => {
    console.log("handleSubtract called");

    if (!points || points.length < 3 || !dentureModels.length) {
      console.error("Error: Not enough points or no denture models found.");
      await Swal.fire("Error", "Please ensure a denture model is selected and enough points are drawn to define the boundary.", "error");
      return;
    }

    console.log("Points and denture models are valid");

    const dentureModel = dentureModels[0]; // Target the first denture model or modify this based on selection
    console.log("Denture model selected:", dentureModel);

    // Ensure the denture model is in Layer B
    if (!dentureModel.tags || !dentureModel.tags.includes("Layer B")) {
      console.warn("Warning: Denture model is not in Layer B.");
      await Swal.fire("Warning", "Brush mode can only be applied to models in Layer B.", "warning");
      return;
    }

    console.log("Denture model is in Layer B");

    // Check if boundary line is defined
    if (!boundaryLineRef.current) {
      console.error("Error: Boundary line reference is not found.");
      await Swal.fire("Error", "Boundary line reference not found.", "error");
      return;
    }

    console.log("Boundary line reference found");

    // Extract the points from the boundaryLineRef
    const boundaryLine = boundaryLineRef.current;
    if (!boundaryLine.geometry || !boundaryLine.geometry.attributes || !boundaryLine.geometry.attributes.position) {
      console.error("Error: Boundary line geometry is not properly defined.");
      await Swal.fire("Error", "Boundary line geometry is invalid.", "error");
      return;
    }

    const positions = boundaryLine.geometry.attributes.position.array; // Access the vertex positions
    console.log("Boundary line positions:", positions);

    // Convert positions (which are a flat array of numbers) into an array of THREE.Vector3
    const boundaryPoints = [];
    for (let i = 0; i < positions.length; i += 3) {
      boundaryPoints.push(new THREE.Vector3(positions[i], positions[i + 1], positions[i + 2]));
    }
    console.log("Boundary points extracted:", boundaryPoints);

    // Create a new Shape object using the boundary points
    const shape = new THREE.Shape();
    boundaryPoints.forEach((point, index) => {
      if (index === 0) {
        shape.moveTo(point.x, point.z); // Start from the first point
      } else {
        shape.lineTo(point.x, point.z); // Draw line to next point
      }
    });
    shape.closePath(); // Close the boundary
    console.log("Shape created from boundary points");

    // Create clipping planes from the boundary shape in XZ
    const clippingPlanes = [];
    for (let i = 0; i < boundaryPoints.length; i++) {
      const p1 = boundaryPoints[i];
      const p2 = boundaryPoints[(i + 1) % boundaryPoints.length]; // Loop around to the first point

      // Create a normal vector for each boundary edge
      const edgeDirection = new THREE.Vector3(p2.x - p1.x, 0, p2.z - p1.z);
      const edgeNormal = new THREE.Vector3(-edgeDirection.z, 0, edgeDirection.x).normalize(); // Normal facing outward

      // Create a plane from the edge
      const plane = new THREE.Plane().setFromNormalAndCoplanarPoint(edgeNormal.negate(), p1); // Flip the normal if needed
      clippingPlanes.push(plane);

      // Add plane helper for debugging
      const planeHelper = new THREE.PlaneHelper(plane, 100, 0xff0000); // Visible helper for clipping plane
      sceneRef.current.add(planeHelper); // Add helper to the scene

      // Make the helper invisible
      planeHelper.visible = false;
    }
    console.log("Clipping planes created:", clippingPlanes);

    // Update the denture model material to respect the clipping planes
    if (!dentureModel.material) {
      console.error("Error: Denture model material not found.");
      return;
    }

    model.material.clippingPlanes = clippingPlanes;
    model.material.clipShadows = true; // Optionally clip shadows
    model.material.transparent = true; // Ensure transparency is enabled
    model.material.opacity = 0.8; // Set opacity to help with visibility
    console.log("Updated denture model material with clipping planes");

    // Enable local clipping in the renderer
    rendererRef.current.localClippingEnabled = true;

    // Store the clipped denture model in state
    setClippedDentureModel(dentureModel);

    // Update the scene with changes
    console.log("Rendering the scene");
    rendererRef.current.render(sceneRef.current, cameraRef.current);
  };


  const createBoundaryShape = (points) => {
    const shape = new THREE.Shape();
    points.forEach((point, index) => {
      if (index === 0) shape.moveTo(point.x, point.z);
      else shape.lineTo(point.x, point.z);
    });
    shape.closePath();
    return shape;
  };

  // Custom isPointInPolygon function using ray-casting algorithm
  const isPointInPolygon = (point, polygon) => {
    let intersects = 0;
    for (let i = 0; i < polygon.length; i++) {
      const v1 = polygon[i];
      const v2 = polygon[(i + 1) % polygon.length];
      if (
        (v1.y > point.y) !== (v2.y > point.y) &&
        point.x < ((v2.x - v1.x) * (point.y - v1.y)) / (v2.y - v1.y) + v1.x
      ) {
        intersects++;
      }
    }
    return intersects % 2 !== 0;
  };

  const clipGeometry = (geometry, shape) => {
    const positions = geometry.attributes.position;
    const verticesToKeep = [];
    const newIndices = [];
    const faceCenters = [];

    // Extract 2D points from shape for polygon containment check
    const shapePoints = shape.getPoints().map((pt) => new THREE.Vector2(pt.x, pt.y));

    // Loop over every three consecutive vertices (non-indexed geometry)
    for (let i = 0; i < positions.count; i += 3) {
      const a = i;
      const b = i + 1;
      const c = i + 2;

      // Calculate the face center
      const faceCenter = new THREE.Vector3(
        (positions.getX(a) + positions.getX(b) + positions.getX(c)) / 3,
        (positions.getY(a) + positions.getY(b) + positions.getY(c)) / 3,
        (positions.getZ(a) + positions.getZ(b) + positions.getZ(c)) / 3
      );

      // Project face center to 2D and check if it’s within the shape using custom isPointInPolygon
      const projectedPoint = new THREE.Vector2(faceCenter.x, faceCenter.z);
      if (isPointInPolygon(projectedPoint, shapePoints)) {
        // Keep this face
        verticesToKeep.push(a, b, c);
        newIndices.push(a, b, c);
      }
    }

    // Create a new geometry with only the kept vertices and faces
    const clippedGeometry = new THREE.BufferGeometry();
    clippedGeometry.setAttribute('position', positions.clone());
    clippedGeometry.setIndex(newIndices);
    clippedGeometry.computeVertexNormals();

    return clippedGeometry;
  };

  // Function to find all intersection points for a list of starting points
  const findAllIntersectionPoints = (modelPoints, dentureModel) => {
    const dentureBackPoints = [];
    const dentureFrontPoints = [];

    modelPoints.forEach(startPoint => {
      const { backPoints, frontPoints } = findIntersectionPoint(startPoint, dentureModel);

      // Only add points if both back and front points are available
      if (backPoints.length > 0 && frontPoints.length > 0) {
        dentureBackPoints.push(...backPoints); // Add all back points
        dentureFrontPoints.push(...frontPoints); // Add all front points
      }
    });

    return { dentureBackPoints, dentureFrontPoints };
  };


  const findIntersectionPoint = (startPoint, dentureModel) => {
    const raycaster = new THREE.Raycaster();
    const direction = new THREE.Vector3(0, 1, 0); // Move upwards along the Y-axis
    const maxDistance = 150; // Max distance to travel along the Y-axis

    let currentPosition = startPoint.clone(); // Start at the original point on the `model`
    const pointsForLine = [currentPosition.clone()]; // Array to store line points for visualization
    const increment = 0.5; // Step size for finding the back point
    const maxDistanceMore = 5; // Step size for moving to front points after back point is found

    const backPoints = [];
    const frontPoints = [];

    // First loop to find the initial back intersection point with small increments (0.5)
    while (currentPosition.y - startPoint.y < maxDistance) {
      currentPosition.add(direction.clone().multiplyScalar(increment)); // Increment upwards by 0.5
      pointsForLine.push(currentPosition.clone()); // Add the current position to the line for visualization

      // Draw the line incrementally as we move upwards
      drawLine22(pointsForLine.slice(-2), 0xff0000); // Draw only the latest segment in red

      // Set up the raycaster to test from the current position upwards
      raycaster.set(currentPosition, direction);
      raycaster.layers.set(2); // Only intersect with objects in Layer 2 (dentureModel)

      const intersects = raycaster.intersectObject(dentureModel);

      if (intersects.length > 0) {
        // Intersection found, capture it as the back point
        const backPoint = intersects[0].point;
        backPoints.push(backPoint); // Store back point
        drawPoint22(backPoint, 0xff0000); // Draw black point at the back intersection

        currentPosition.add(direction.clone().multiplyScalar(1)); // Move up by 1 units

        // Nested loop to find front points in larger steps of 5 units
        while (currentPosition.y - startPoint.y < maxDistanceMore) {
          currentPosition.add(direction.clone().multiplyScalar(increment)); // Move up by 5 units

          // Set up the raycaster again to test from the updated position upwards
          raycaster.set(currentPosition, direction);
          raycaster.layers.set(2); // Only intersect with objects in Layer 2 (dentureModel)
          const frontIntersects = raycaster.intersectObject(dentureModel);

          if (frontIntersects.length > 0) {
            // Capture each additional intersection as a front point
            frontIntersects.forEach((intersect) => {
              const frontPoint = intersect.point;
              frontPoints.push(frontPoint); // Store front point
              drawPoint22(frontPoint, 0xff0000); // Draw green point for each front intersection

            });
          } else {
            // No further intersections found; exit the nested loop
            break;
          }
        }
        // Exit the initial loop since we've handled both back and front points
        break;
      }
    }

    return { backPoints, frontPoints }; // Return both back and front points
  };


  // Function to draw a single line segment
  const drawLine22 = (pointsArray, color) => {
    const geometry = new THREE.BufferGeometry().setFromPoints(pointsArray);
    const material = new THREE.LineBasicMaterial({ color });
    const line = new THREE.Line(geometry, material);
    sceneRef.current.add(line);
  };

  // Function to draw a single point
  const drawPoint22 = (point, color) => {
    const geometry = new THREE.SphereGeometry(0.2, 8, 8); // Adjust size as needed
    const material = new THREE.MeshBasicMaterial({ color });
    const sphere = new THREE.Mesh(geometry, material);
    sphere.position.copy(point);
    sceneRef.current.add(sphere);
  };

  const handleSubtract = async () => {


    console.log("handleSubtract called");

    if (!points || points.length < 3 || !dentureModels.length) {
      console.error("Error: Not enough points or no denture models found.");
      await Swal.fire("Error", "Please ensure a denture model is selected and enough points are drawn to define the boundary.", "error");
      return;
    }

    console.log("Points and denture models are valid");

    const dentureModel = dentureModels[0]; // Target the first denture model or modify this based on selection
    console.log("Denture model selected:", dentureModel);

    // Ensure the denture model is in Layer B
    if (!dentureModel.tags || !dentureModel.tags.includes("Layer B")) {
      console.warn("Warning: Denture model is not in Layer B.");
      await Swal.fire("Warning", "Brush mode can only be applied to models in Layer B.", "warning");
      return;
    }

    console.log("Denture model is in Layer B");

    setLoading(true); // Show loading spinner or update UI as loading

    // Store the original position in a variable
    const originalPositionY = dentureModel.position.y;

    // Move the model up by 50 units
    dentureModel.position.y += 50;
    dentureModel.updateMatrixWorld(); // Update the world matrix

    // Wait for a brief moment before proceeding
    await new Promise((resolve) => setTimeout(resolve, 500)); // Wait 0.5 seconds

    // Get all intersection points by passing model points and the dentureModel to `findAllIntersectionPoints`
    const { dentureBackPoints, dentureFrontPoints } = findAllIntersectionPoints(points, dentureModel);




    if (dentureBackPoints.length === 0 || dentureFrontPoints.length === 0) {
      setLoading(false); // Hide loading spinner or update UI as not loading
      console.error("No intersections found.");
      return;
    }

    if (dentureBackPoints.length < 3 || dentureFrontPoints.length < 3) {
      setLoading(false); // Hide loading spinner or update UI as not loading
      console.error("Error: Not enough points to form a boundary on denture model.");
      await Swal.fire("Error", "Failed to project points onto the denture model.", "error");
      return;
    }

    // Prepare points and STL blob for API call
    const dentureModelFrontPointsData = dentureFrontPoints.map((p) => ({ x: p.x, y: p.y, z: p.z }));
    const dentureModelBackPointsData = dentureFrontPoints.map((p) => ({ x: p.x, y: p.y, z: p.z }));
    const exporter = new STLExporter();
    const stlBlob = new Blob([exporter.parse(dentureModel)], { type: "application/octet-stream" });

    // Set up form data
    const formData = new FormData();
    formData.append("stl", stlBlob, "objectA.stl");
    formData.append("fpoints", JSON.stringify(dentureModelFrontPointsData));
    formData.append("bpoints", JSON.stringify(dentureModelBackPointsData));



    try {
      // Call the cut model API
      const response = await fetch("https://3.144.201.98/api/cut-model", {
        method: "POST",
        body: formData,
      });

      if (response.ok) {
        // Process the returned STL model
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);

        // Load the modified STL into the scene
        loadModifiedSTL(url, dentureModel);
        // sceneRef.current.remove(dentureModel); // Remove original model
      } else {
        console.error("Cut operation failed:", response.statusText);
      }
    } catch (error) {
      console.error("Error calling cut API:", error);
    } finally {
      setLoading(false); // Hide loading spinner or update UI as not loading
    }

  };


  // Function to load modified STL model back into the scene
  const loadModifiedSTL = (stlUrl, oldDentureModel) => {
    const loader = new STLLoader();
    loader.load(stlUrl, (geometry) => {
      const material = new THREE.MeshStandardMaterial({
        color: 0xffff00, // Update to desired color for the modified model
        metalness: 0.5,
        roughness: 0.5,
        side: THREE.DoubleSide,
      });

      const modifiedMesh = new THREE.Mesh(geometry, material);
      modifiedMesh.name = "Modified Denture Model";
      modifiedMesh.position.set(0, 0, 0); // Adjust position as needed

      // Clear previous model and add modified model
      // Remove previous denture model from the scene
      sceneRef.current.remove(oldDentureModel);
      sceneRef.current.add(modifiedMesh);    // Add modified model

      attachTransformControls(modifiedMesh);
      // Update the state with the new model
      setDentureModels([modifiedMesh]);
    });
  };
  // Helper function to clean up and sanitize geometry
  const sanitizeMesh = (mesh) => {
    const geometry = mesh.geometry;

    // Remove unnecessary attributes (e.g., uv, morph targets, etc.)
    for (const name in geometry.attributes) {
      if (name !== 'position' && name !== 'normal' && name !== 'index') {
        console.log(`Removing unused attribute: ${name}`);
        geometry.deleteAttribute(name);
      }
    }

    // Ensure normals are computed
    if (!geometry.attributes.normal) {
      geometry.computeVertexNormals();
    }

    // Ensure geometry is indexed
    if (!geometry.index) {
      console.log("Converting geometry to indexed geometry...");
      const indices = [...Array(geometry.attributes.position.count).keys()];
      geometry.setIndex(indices);
    }

    // Remove morph targets from the mesh (not just geometry)
    if (mesh.morphTargetInfluences || mesh.morphTargetDictionary) {
      console.log("Removing morph targets from mesh.");
      mesh.morphTargetInfluences = null;
      mesh.morphTargetDictionary = null;
    }

    // Make sure morph attributes are cleared if they exist
    if (geometry.morphAttributes) {
      geometry.morphAttributes = {}; // Clear morph attributes
      geometry.morphTargetsRelative = false;
    }

    console.log("Mesh sanitized and prepared for CSG.");
  };

  // Function to toggle merge/unmerge mode
  const toggleMergeMode = async () => {

    if (!model) {
      await Swal.fire("Warning", "Please upload a model first to enter merge mode.", "warning");
      return;
    }

    if (!clippedDentureModel) {
      Swal.fire("Error", "Clipped denture model must be available to Unmerge with the main model.", "error");
      return;
    }


    if (isMergeMode) {
      await handleUnmerge();  // Call unmerge if currently merged
    } else {
      await handleMerge();  // Call merge if currently unmerged
    }

    setIsMergeMode((prev) => !prev);  // Toggle the isMergeMode state
  };


  const handleUnmerge = async () => {
    if (!mergedModel) {
      Swal.fire("Error", "No merged model found to unmerge.", "error");
      return;
    }

    console.log("Unmerging model:", mergedModel);

    // Remove the merged model from the scene
    sceneRef.current.remove(mergedModel);

    // Add the original models back to the scene
    originalModels.forEach((originalModel) => {
      sceneRef.current.add(originalModel);
    });

    // Update state to track the original models
    setModel(originalModels[0]); // Update state with the original model
    setClippedDentureModel(originalModels[1]);
    setMergedModel(null); // Clear merged model state
    setOriginalModels([]); // Clear stored original models

    console.log("Original models restored to the scene");
  };


  const removeAll = () => {
    removeBoundaryLines();
    removeDrawingLines();
    removeDrawingPoints();
    console.log("All relevant objects removed from the scene.");
  };

  // Remove boundary lines by their specific names
  const removeBoundaryLines = () => {
    const boundaryLineNames = [
      "Points Boundary Line",
      "Points Cross Boundary Line",
      "Denture Boundary Line",
      "Denture Cross Boundary Line"
    ];
    removeObjectsByName(boundaryLineNames);
  };

  // Remove all lines named "DrawingLine"
  const removeDrawingLines = () => {
    removeAllObjectsByName("DrawingLine");
  };

  // Remove all points named "DrawingPoint"
  const removeDrawingPoints = () => {
    removeAllObjectsByName("DrawingPoint");
  };

  // Helper function to remove multiple objects by name
  const removeObjectsByName = (names) => {
    names.forEach((name) => {
      removeAllObjectsByName(name);
    });
  };

  // Helper function to remove all objects with a given name
  const removeAllObjectsByName = (name) => {
    let objectsToRemove = [];

    // Find all objects with the given name
    sceneRef.current.traverse((child) => {
      if (child.name === name) {
        objectsToRemove.push(child);
      }
    });

    // Remove and dispose each object
    objectsToRemove.forEach((objectToRemove) => {
      sceneRef.current.remove(objectToRemove);
      if (objectToRemove.geometry) objectToRemove.geometry.dispose();  // Dispose of geometry
      if (objectToRemove.material) objectToRemove.material.dispose();  // Dispose of material
    });

    console.log(`All objects named '${name}' removed from the scene.`);
  };



  const handleMerge = async () => {
    if (!model) {
      await Swal.fire("Warning", "Please upload a model first to enter merge mode.", "warning");
      return;
    }

    if (!clippedDentureModel) {
      Swal.fire("Error", "Clipped denture model must be available to merge with the main model.", "error");
      return;
    }

    console.log("Merging model:", model);
    console.log("Merging clipped denture model:", clippedDentureModel);

    // Extract geometries from the model and clipped denture model
    let modelGeometry = model.geometry.clone(); // Clone to avoid modifying original geometry
    let dentureGeometry = clippedDentureModel.geometry.clone();

    // Ensure both geometries are BufferGeometry
    if (!(modelGeometry instanceof THREE.BufferGeometry) || !(dentureGeometry instanceof THREE.BufferGeometry)) {
      Swal.fire("Error", "Both models must have BufferGeometry to merge.", "error");
      return;
    }

    // Apply the world matrix to geometry so that their transformations (position, rotation, scale) are baked into the geometry
    model.updateMatrixWorld(); // Ensure the world matrix is up to date
    dentureGeometry.applyMatrix4(clippedDentureModel.matrixWorld); // Apply transformation of dentureModel
    modelGeometry.applyMatrix4(model.matrixWorld); // Apply transformation of original model

    // Merge the geometries using mergeBufferGeometries
    const mergedGeometry = mergeBufferGeometries([modelGeometry, dentureGeometry], true);
    if (!mergedGeometry) {
      Swal.fire("Error", "Failed to merge geometries.", "error");
      return;
    }

    // Create a new material or reuse an existing one
    const mergedMaterial = new THREE.MeshStandardMaterial({
      color: 0xd4667f,  // Example color
      metalness: 0.5,
      roughness: 0.5,
      side: THREE.DoubleSide // Render both sides
    });

    // Create a new mesh with the merged geometry
    const mergedMesh = new THREE.Mesh(mergedGeometry, mergedMaterial);
    mergedMesh.name = "Merged Model";  // Assign a name to the merged mesh

    // Set merged mesh at origin and reset its transformations (since they are baked into geometry)
    mergedMesh.position.set(0, 0, 0);
    mergedMesh.rotation.set(0, 0, 0);
    mergedMesh.scale.set(1, 1, 1);
    mergedMesh.updateMatrixWorld(); // Update the matrix world after resetting transformations

    // Store the original models for unmerging later
    setOriginalModels([model, clippedDentureModel]);

    // Remove boundary and cross lines from the scene before merging
    cleanUpAfterAdaptation();
    removeAll();
    // Remove the original models from the scene
    sceneRef.current.remove(model);
    sceneRef.current.remove(clippedDentureModel);

    // Add the merged mesh to the scene
    sceneRef.current.add(mergedMesh);

    // Update state to track the merged model
    setMergedModel(mergedMesh);
    setModel(mergedMesh);  // Update model reference
    setClippedDentureModel(null);   // Clear the clippedDentureModel state


    // Remove any UI elements like buttons related to this specific model
    const buttonContainer = document.querySelector('.ui-model-buttons');  // Select the element with class "ui-model-buttons"
    if (buttonContainer) {
      buttonContainer.remove();  // Remove button container only related to this model
    }

    console.log("Merged model added to the scene");
  };



  function mergeBufferGeometries(geometries, useGroups = false) {
    if (geometries.length === 0) {
      console.error("No geometries provided for merging");
      return null;
    }

    // Ensure geometries are instances of BufferGeometry
    geometries = geometries.filter(geometry => geometry instanceof THREE.BufferGeometry);
    if (geometries.length === 0) {
      console.error("No valid BufferGeometry provided for merging");
      return null;
    }

    let mergedGeometry = new THREE.BufferGeometry();
    const attributesUsed = Object.keys(geometries[0].attributes);
    const mergedAttributes = {};
    let mergedIndex = [];
    let offset = 0;

    // Loop through each geometry to handle attributes
    for (let i = 0; i < geometries.length; i++) {
      const geometry = geometries[i];

      // Update index if geometry is indexed
      if (geometry.index) {
        const indexArray = geometry.index.array;
        for (let j = 0; j < indexArray.length; j++) {
          mergedIndex.push(indexArray[j] + offset);
        }
      }

      // Loop over each attribute type (position, normal, uv, etc.)
      for (let name of attributesUsed) {
        if (!geometry.attributes[name]) {
          console.warn(`Attribute ${name} missing in geometry ${i}, skipping attribute merge for this geometry.`);
          continue;
        }

        const attribute = geometry.attributes[name];
        if (!mergedAttributes[name]) {
          // Calculate the total length for merged attribute array
          let totalLength = 0;
          for (let geom of geometries) {
            if (geom.attributes[name]) {
              totalLength += geom.attributes[name].array.length;
            }
          }

          // Initialize a new Float32Array for the merged attribute
          mergedAttributes[name] = {
            array: new Float32Array(totalLength),
            itemSize: attribute.itemSize,
            offset: 0,
          };
        }

        // Copy attribute array data into the merged attribute array
        mergedAttributes[name].array.set(attribute.array, mergedAttributes[name].offset);
        mergedAttributes[name].offset += attribute.array.length;
      }

      // Update offset for indexed geometries
      offset += geometry.attributes.position.count;
    }

    // Set merged attributes to the merged geometry
    for (let name of attributesUsed) {
      if (mergedAttributes[name]) {
        mergedGeometry.setAttribute(
          name,
          new THREE.BufferAttribute(mergedAttributes[name].array, mergedAttributes[name].itemSize)
        );
      }
    }

    // Set index if available
    if (mergedIndex.length > 0) {
      mergedGeometry.setIndex(mergedIndex);
    }

    // Add groups if useGroups is true
    if (useGroups) {
      let groupOffset = 0;
      for (let i = 0; i < geometries.length; i++) {
        const count = geometries[i].index ? geometries[i].index.count : geometries[i].attributes.position.count;
        mergedGeometry.addGroup(groupOffset, count, i);
        groupOffset += count;
      }
    }

    return mergedGeometry;
  }



  // Add mouse move listener
  useEffect(() => {
    const handleMouseMoveWithTorch = (event) => {
      handleMouseMove(event);
    };

    // Handle keydown and keyup events to track ALT key state
    const handleKeyDown = (event) => {
      if (event.key === 'Alt') {
        orbitControlsRef.current.enabled = false;
        setIsAltPressed(true);
      }
    };

    const handleKeyUp = (event) => {
      if (event.key === 'Alt') {
        orbitControlsRef.current.enabled = true;
        setIsAltPressed(false);
      }
    };

    window.addEventListener('mousemove', handleMouseMoveWithTorch);

    // Add keydown and keyup listeners for ALT key
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);


    return () => {
      window.removeEventListener('mousemove', handleMouseMoveWithTorch);
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);

    };
  }, [isTorchMode]); // Dependency array includes isTorchMode


  return (
    <div style={{ display: 'flex' }}>
      <Toolbox
        handleModelUpload={handleModelUpload}
        toggleDrawingMode={toggleDrawingMode}
        isDrawing={isDrawing}
        toggleTransformMode={toggleTransformMode}
        transformMode={transformMode}
        toggleRotateMode={toggleRotateMode}
        rotateMode={rotateMode}
        exportModel={exportModel}
        adaptDenture={adaptDenture}
        toggleTorchMode={toggleTorchMode}
        isTorchMode={isTorchMode}
        toggleMergeMode={toggleMergeMode}
        isMergeMode={isMergeMode}
        handleIntersect={handleSubtract}  // Pass the intersect handler

      />
      <ModelSelector scene={sceneRef.current} onModelSelected={handleModelSelected} />
      <div
        ref={mountRef}
        style={{ flex: 1, height: '100vh' }}
        onClick={handleMouseClick}
      />
      <Footer />

      {/* Show Loading Spinner if loading is true */}
      {loading && <LoadingSpinner />}


    </div>
  );
};

export default App;
